import Dexie, { type EntityTable } from 'dexie'
import type { CaptureMode, CommandResult, CommandType } from './protocol'
import type { SealedBox } from './vault'

/**
 * The phone's IndexedDB. One database per origin, versioned with Dexie.
 *
 * MIGRATIONS: never edit an existing `.version(n).stores()` block once shipped. Add
 * `.version(n + 1).stores({...changed tables only})` with an `.upgrade()` if rows need rewriting.
 */

// ---------- outbox ----------

export const OUTBOX_STATUSES = [
  'PENDING', // waiting to be sent (also: transient failure, retry at next_attempt_at)
  'SENDING', // in flight; reset to PENDING on start-up if the app died mid-request
  'APPLIED', // server applied it
  'DUPLICATE', // server had it already — same as applied
  'HELD', // server applied it and put the lot/session on hold (exception_ids say why)
  'WAIT_FOR_PARENT', // server needs an earlier command first; retried with backoff
  'ERROR', // will not succeed by resending (malformed / conflict) — a person must look
  'SET_ASIDE', // a supervisor took it (and what depends on it) out of the queue; kept, never sent
] as const
export type OutboxStatus = (typeof OUTBOX_STATUSES)[number]

export interface OutboxRecord {
  /** Auto-increment: the order the commands were made. Sync sends in this order. */
  seq?: number
  /** UUIDv7 made on the phone. The server's idempotency key. */
  id: string
  type: CommandType
  actor_id: string
  occurred_at: string
  session_id: string | null
  capture_mode: CaptureMode
  payload: unknown
  /** Commands that must reach the server first (WAIT_FOR_PARENT otherwise). */
  parent_ids: string[]
  /** Photos this command references; uploaded before the command is sent. */
  media_ids: string[]
  created_at: number
  status: OutboxStatus
  attempts: number
  /** Epoch ms; not sent before this (backoff). */
  next_attempt_at: number
  last_error: string | null
  last_error_code: string | null
  result: CommandResult | null
  acked_at: number | null
  /** SET_ASIDE only (no index, so no schema version): when, who confirmed, why, and the record the supervisor picked. */
  set_aside?: SetAsideInfo
}

export interface SetAsideInfo {
  at: number
  by_user_id: string | null
  by_name: string | null
  reason: string
  /** The ERROR record that was set aside; equals the record's own id for that one. */
  root_id: string
  /** Status before it was set aside, restored by "put back". */
  previous_status: OutboxStatus
}

// ---------- caches ----------

/**
 * A cached row. `data` is the plain record, or a SealedBox when the roster vault is on. Only the
 * id (and an optional `ref` foreign key) is indexed — names and phone digits are never indexed so
 * they can stay encrypted; the roster is small enough to search in memory.
 */
export interface CacheRow<T = unknown> {
  id: string
  ref?: string
  data: T | SealedBox
}

// ---------- drafts ----------

/** In-progress work (a delivery being weighed, a loading list) that must survive reloads. */
export interface DraftRecord<T = unknown> {
  id: string
  /** e.g. 'gate.delivery', 'store.loading'. */
  kind: string
  actor_id: string | null
  session_id: string | null
  data: T
  created_at: number
  updated_at: number
}

// ---------- media ----------

/**
 * LOST: the photo's bytes can no longer be read on this phone (an iPhone browser storage fault).
 * No upload can fix it; it is not retried by itself and the Sync screen says so.
 */
export type MediaStatus = 'PENDING' | 'UPLOADING' | 'UPLOADED' | 'ERROR' | 'LOST'

export interface MediaRecord {
  /** UUIDv7 made on the phone; the command stores this id in its photo column. */
  id: string
  /** Dropped after upload to free space; metadata is kept for the audit view. */
  blob: Blob | null
  mime: string
  size: number
  created_at: number
  status: MediaStatus
  attempts: number
  next_attempt_at: number
  last_error: string | null
  uploaded_at: number | null
}

// ---------- meta ----------

export interface MetaRecord {
  key: string
  value: unknown
}

export const ROSTER_TABLES = [
  'farmers',
  'fields',
  'rate_cards',
  'sack_types',
  'rolls',
  'team',
  'config',
  'locations',
] as const
export type RosterTable = (typeof ROSTER_TABLES)[number]

export class SupplyChainDB extends Dexie {
  outbox!: EntityTable<OutboxRecord, 'seq'>
  drafts!: EntityTable<DraftRecord, 'id'>
  media!: EntityTable<MediaRecord, 'id'>
  meta!: EntityTable<MetaRecord, 'key'>
  // roster cache (village devices) — GET /devices/me/roster
  farmers!: EntityTable<CacheRow, 'id'>
  fields!: EntityTable<CacheRow, 'id'>
  rate_cards!: EntityTable<CacheRow, 'id'>
  sack_types!: EntityTable<CacheRow, 'id'>
  rolls!: EntityTable<CacheRow, 'id'>
  team!: EntityTable<CacheRow, 'id'>
  config!: EntityTable<CacheRow, 'id'>
  // site cache (store devices) — GET /sites/{site_id}/store-cache
  locations!: EntityTable<CacheRow, 'id'>

  constructor(name = 'uc-supplychain') {
    super(name)
    this.version(1).stores({
      outbox: '++seq, &id, status, type, session_id, created_at',
      drafts: '&id, kind, updated_at',
      media: '&id, status, created_at',
      meta: '&key',
      farmers: '&id',
      fields: '&id, ref',
      rate_cards: '&id',
      sack_types: '&id',
      rolls: '&id',
      team: '&id',
      config: '&id',
      locations: '&id, ref',
    })
  }
}

let instance: SupplyChainDB | null = null

/** The app's database. Tests construct their own `new SupplyChainDB('test-…')`. */
export function getDb(): SupplyChainDB {
  instance ??= new SupplyChainDB()
  return instance
}
